;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:propstrata-app":true});}catch(e){}}();import type { Breadcrumb, ErrorEvent, EventHint, Log } from "@sentry/nextjs";
import * as Sentry from "@sentry/nextjs";
import {
	type EmittedLogLevel,
	isLogLevelEnabled,
	type LogLevelSetting,
	type ObservabilityConfig,
} from "@/lib/observability/environment";
import { recordMetric } from "@/lib/observability/metric-registry";
import {
	getIgnoredExceptionReason,
	isThirdPartyInstallationError,
	SENTRY_DATA_COLLECTION,
	scrubBreadcrumb,
	scrubSensitiveString,
	scrubSensitiveValue,
} from "@/lib/observability/scrub";

const DROPPED_TRACE_PATH_PREFIXES = [
	"/api/health",
	"/api/realtime/events",
	"/api/usage-tracking",
	"/monitoring",
	"/_next/static",
	"/_next/image",
	"/favicon",
] as const;

// Liveness probes, e.g. `/api/workspaces/{id}/data-provider/ping`.
const DROPPED_TRACE_PATH_SUFFIXES = ["/ping"] as const;

const DROPPED_TRACE_METHODS = new Set(["OPTIONS", "HEAD"]);

// Low volume and each run matters: trace all of them, even when a publisher
// sampled at the base rate sent its trace headers along with the task.
const FULLY_TRACED_PATH_PREFIXES = ["/api/scheduler", "/api/tasks"] as const;

// SDK 11 samples at span start, before the final name is known.
const TRACE_PATH_ATTRIBUTE_KEYS = [
	"http.route",
	"http.target",
	"url.path",
] as const;

const TRACE_URL_ATTRIBUTE_KEYS = ["http.url", "url.full"] as const;

const TRACE_METHOD_ATTRIBUTE_KEYS = [
	"http.request.method",
	"http.method",
] as const;

const HTTP_METHOD_NAME_PATTERN = /^([A-Z]+)\s+(\/\S*)/;

export type SentrySamplingContext = {
	attributes?: Record<string, unknown>;
	inheritOrSampleWith?: (fallbackSampleRate: number) => number;
	name?: string;
	parentSampled?: boolean;
};

function readStringAttribute({
	attributeKeys,
	samplingContext,
}: {
	attributeKeys: readonly string[];
	samplingContext: SentrySamplingContext;
}): string[] {
	return attributeKeys
		.map((attributeKey) => samplingContext.attributes?.[attributeKey])
		.filter((attributeValue): attributeValue is string => {
			return typeof attributeValue === "string" && attributeValue !== "";
		});
}

function stripQuery(path: string): string {
	return path.split(/[?#]/, 1)[0] ?? "";
}

function toPathname(url: string): string | undefined {
	try {
		return new URL(url).pathname;
	} catch {
		return undefined;
	}
}

/** Pathnames only, so a `?returnTo=/api/…` query can't change the rate. */
function getTransactionPaths(samplingContext: SentrySamplingContext): string[] {
	const namePath = HTTP_METHOD_NAME_PATTERN.exec(
		samplingContext.name ?? "",
	)?.[2];
	const candidatePaths = [
		...(namePath ? [namePath] : []),
		...(samplingContext.name?.startsWith("/") ? [samplingContext.name] : []),
		...readStringAttribute({
			attributeKeys: TRACE_PATH_ATTRIBUTE_KEYS,
			samplingContext,
		}),
		...readStringAttribute({
			attributeKeys: TRACE_URL_ATTRIBUTE_KEYS,
			samplingContext,
		})
			.map(toPathname)
			.filter((pathname): pathname is string => pathname !== undefined),
	];
	return candidatePaths.map((path) => stripQuery(path).toLowerCase());
}

function getTransactionMethod(
	samplingContext: SentrySamplingContext,
): string | undefined {
	const [attributeMethod] = readStringAttribute({
		attributeKeys: TRACE_METHOD_ATTRIBUTE_KEYS,
		samplingContext,
	});
	const method =
		attributeMethod ??
		HTTP_METHOD_NAME_PATTERN.exec(samplingContext.name ?? "")?.[1];
	return method?.toUpperCase();
}

function isDroppedTransaction(samplingContext: SentrySamplingContext): boolean {
	const method = getTransactionMethod(samplingContext);
	if (method && DROPPED_TRACE_METHODS.has(method)) {
		return true;
	}
	return getTransactionPaths(samplingContext).some(
		(path) =>
			DROPPED_TRACE_PATH_PREFIXES.some((prefix) => path.startsWith(prefix)) ||
			DROPPED_TRACE_PATH_SUFFIXES.some((suffix) => path.endsWith(suffix)),
	);
}

function isFullyTracedTransaction(
	samplingContext: SentrySamplingContext,
): boolean {
	return getTransactionPaths(samplingContext).some((path) =>
		FULLY_TRACED_PATH_PREFIXES.some((prefix) => path.startsWith(prefix)),
	);
}

export function getTraceSampleRate({
	baseRate,
	samplingContext,
}: {
	baseRate: number;
	samplingContext: SentrySamplingContext;
}): number {
	if (isDroppedTransaction(samplingContext)) {
		return 0;
	}

	if (isFullyTracedTransaction(samplingContext)) {
		return 1;
	}

	// Inherits the parent decision and rate so client → server traces stay whole.
	if (samplingContext.inheritOrSampleWith) {
		return samplingContext.inheritOrSampleWith(baseRate);
	}
	if (samplingContext.parentSampled !== undefined) {
		return samplingContext.parentSampled ? 1 : 0;
	}
	return baseRate;
}

/**
 * The SDK types `message` as a string, but events built by hand or by some
 * integrations carry `{ formatted, message }`.
 */
function scrubEventMessage(message: unknown): unknown {
	if (typeof message === "string") {
		return scrubSensitiveString(message);
	}
	if (!message || typeof message !== "object") {
		return message;
	}
	const structuredMessage = message as Record<string, unknown>;
	const scrubbedMessage = { ...structuredMessage };
	for (const key of ["formatted", "message"]) {
		const text = structuredMessage[key];
		if (typeof text === "string") {
			scrubbedMessage[key] = scrubSensitiveString(text);
		}
	}
	return scrubbedMessage;
}

export function buildBeforeSend({ isBrowser }: { isBrowser: boolean }) {
	return (event: ErrorEvent, hint: EventHint): ErrorEvent | null => {
		const ignoredReason = getIgnoredExceptionReason(hint.originalException);
		// On the server a network error is an outbound call failing: keep it.
		const isIgnored =
			ignoredReason !== null && (isBrowser || ignoredReason !== "network");
		if (isIgnored) {
			// Too noisy for Issues, but a spike is the only browser-side outage signal.
			if (ignoredReason === "network") {
				recordMetric({ attributes: {}, name: "client.network_failure" });
			}
			return null;
		}
		if (isThirdPartyInstallationError(event)) {
			return null;
		}

		if (event.message) {
			event.message = scrubEventMessage(event.message) as ErrorEvent["message"];
		}
		for (const exceptionValue of event.exception?.values ?? []) {
			if (exceptionValue.value) {
				exceptionValue.value = scrubSensitiveString(exceptionValue.value);
			}
		}
		if (event.extra) {
			event.extra = scrubSensitiveValue(event.extra) as typeof event.extra;
		}
		if (event.contexts) {
			event.contexts = scrubSensitiveValue(
				event.contexts,
			) as typeof event.contexts;
		}
		if (event.request) {
			event.request = scrubSensitiveValue(
				event.request,
			) as typeof event.request;
		}
		if (event.breadcrumbs) {
			event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb);
		}
		return event;
	};
}

export function buildBeforeSendLog({
	logLevel,
}: {
	logLevel: LogLevelSetting;
}) {
	return (log: Log): Log | null => {
		if (
			!isLogLevelEnabled({
				level: log.level as EmittedLogLevel,
				threshold: logLevel,
			})
		) {
			return null;
		}
		// A template string from `logger.fmt` is a String object; keep it unless it changes.
		const messageText = String(log.message ?? "");
		const scrubbedMessage = scrubSensitiveString(messageText);
		if (scrubbedMessage !== messageText) {
			log.message = scrubbedMessage;
		}
		if (log.attributes) {
			log.attributes = scrubSensitiveValue(log.attributes) as Log["attributes"];
		}
		return log;
	};
}

/**
 * Spans that add volume without telling us anything: Prisma's per-query
 * internals (the SQL span under them is kept), pool checkouts, and Next.js
 * request plumbing that duplicates the route's own span. Children of a
 * dropped span move up to its parent.
 */
const NOISE_SPAN_NAMES = [
	/^prisma:client:(operation|serialize|compile)$/,
	/^prisma:engine:/,
	"pg-pool.connect",
	/^resolve page components$/,
	/^start response$/,
	/^executing api route \(app\) /,
];

export function buildSharedInitOptions({
	config,
	dsn,
}: {
	config: ObservabilityConfig;
	dsn: string | undefined;
}) {
	return {
		beforeBreadcrumb: (breadcrumb: Breadcrumb) => scrubBreadcrumb(breadcrumb),
		beforeSend: buildBeforeSend({ isBrowser: config.service === "browser" }),
		beforeSendLog: buildBeforeSendLog({ logLevel: config.logLevel }),
		dataCollection: SENTRY_DATA_COLLECTION,
		debug: false,
		dsn,
		environment: config.environment,
		ignoreSpans: NOISE_SPAN_NAMES,
		release: config.release,
		tracesSampler: (samplingContext: SentrySamplingContext) =>
			getTraceSampleRate({
				baseRate: config.traceSampleRate,
				samplingContext,
			}),
	};
}

/** Call once right after `Sentry.init` in each runtime. */
export function applyGlobalAttributes(config: ObservabilityConfig): void {
	const globalScope = Sentry.getGlobalScope();
	globalScope.setAttributes({
		service: config.service,
		...(config.revision && { "service.revision": config.revision }),
	});
	globalScope.setTag("service", config.service);
}
